/* ============================================================================
   STICKY BAR — Nintara Baby

   PRODUCT MODE  (.sticky-atc-wrapper rendered = product template, non gift-card)
     HANDOVER RULE — "is THIS product in the cart?"
     • Fresh landing -> entire bar off-screen… EXCEPT an empty cart, where offer
       callout #1 shows from the first fold.
     • Reveal: the floating ATC has slid UNDER the sticky header, OR this product
       is in the cart.
     • Not in cart -> ATC pill (full width at 0 items; shrinks + cart icon with
       count bubble when the cart already has items from an earlier PDP).
     • In cart -> View Cart, at any scroll position. SURVIVES A REFRESH.
     • Removed from the cart -> the ATC pill correctly comes back.
     • SOLD-OUT product:
         - EMPTY cart (landed directly on the OOS PDP) -> a DISABLED "Out of
           stock" pill (60% opacity, non-clickable) that behaves exactly like the
           ATC pill: hidden at the top, revealed after the fold. No cart icon.
         - Cart HAS items -> View Cart (normal handover). So the OOS message only
           shows when landing on the OOS PDP with an empty cart; otherwise it's
           View Cart.

   CART MODE  (index / collection / search / page.* / gift-card PDP)
     • Bar always on-screen, View Cart shows when the cart has items.

   FIRST PAINT is Liquid's job — active offer step, View Cart row, cart badge AND
   the strikethrough compare-at total are all server-rendered. This file only
   handles CHANGES.

   REVEAL THRESHOLD (header-aware) — the header is STICKY now. The floating ATC
   is visually gone the moment it slides under the header, not when it clears the
   top of the viewport, so the old `bottom < 0` test left a dead zone where
   neither button was on screen. isAfterFold() now measures against the header's
   bottom edge. See STICKY_HEADER_SELECTOR below.

   VARIANT CORRECTNESS: the sticky ATC id is re-resolved at click time (capture
   phase) so it always posts the size/variant the shopper actually selected.

   SPEED MODEL: no idle poll. Cart is re-read on load, on real /cart/* mutations
   (fetch + XHR interceptor), on any [data-action-atc] click (backstop), and on
   tab refocus. This keeps the network/main thread free when the user taps, so
   the drawer opens without contention.

   DRAWER OPEN: the sticky icon / View Cart are <a href="/cart"> fallbacks. This
   theme opens its mini-cart via a handler bound to the header trigger
   #nin_web_cart (which itself has NO data-drawer-options), NOT via a generic
   data-drawer-options listener. So a raw sticky click falls through to the href
   and NAVIGATES (the page-load-then-cart flash). We forward the tap to
   #nin_web_cart and preventDefault so nothing navigates.

   Emits `sticky-cart:updated` so offer-call.js can drop its duplicate 1s poll.
   ============================================================================ */

document.addEventListener('DOMContentLoaded', () => {
  const CFG = window.__stickyCartConfig || {};

  const DEBUG = false;
  const log = (...a) => { if (DEBUG) console.log(...a); };

  // ══════════════════════════════════════════════════════════════════════════
  // TUNABLES
  // ══════════════════════════════════════════════════════════════════════════

  /* STICKY HEADER — the element that actually stays pinned at the top.
     Leave '' to auto-detect (see resolveStickyHeader). Pinning it explicitly is
     one devtools check and removes all guesswork, e.g.:
         const STICKY_HEADER_SELECTOR = '#shopify-section-header';
     If nothing sticky is found we fall back to the plain <header>, whose
     rect.bottom goes negative once it scrolls away and gets clamped to 0 —
     i.e. exactly the ORIGINAL pre-sticky-header behaviour. */
  const STICKY_HEADER_SELECTOR = '';

  /* Dead band on the reveal test, in px. iOS collapses/expands the URL bar on
     tap and the drawer scroll-locks <body>; both nudge scroll metrics. Without
     a dead band the fold test can flip for a single frame, is-bar-hidden
     toggles, and the bar runs its .25s transform down-and-back — which is what
     reads as the cart icon "vibrating" on iPhone 12/13/14. */
  const FOLD_HYSTERESIS = 24;

  /* How long to hold the bar's state after a tap that opens the drawer. Covers
     the drawer animation + the body scroll-lock reflow. */
  const DRAWER_FREEZE_MS = 600;

  // ── ELEMENTS ──────────────────────────────────────────────────────────────
  const stickyBar            = document.getElementById('sticky-view-cart');
  const stickyCart           = document.getElementById('view-cart-inner');
  const cartQuantityElem     = document.getElementById('cart-quantity');
  const cartTotalElem        = document.getElementById('cart-total');
  const cartCompareTotalElem = document.getElementById('cart-comparetotal');

  const stickyATC       = document.querySelector('.sticky-atc-wrapper');  // null off PDP
  const stickyATCBtn    = document.querySelector('.sticky-atc-btn');
  const stickyForm      = document.getElementById('sticky-atc-form');
  const stickyCartCount = document.getElementById('sticky-cart-count');

  // Derived from what actually rendered — makes gift-card PDPs and the
  // -amt / non-amt snippet split both fall out correctly with no extra flags.
  const PRODUCT_MODE = !!stickyATC;

  // Gift-card PDPs render no offer callouts. If View Cart is also down, the bar
  // has nothing in it — don't leave an empty white strip with a shadow on screen.
  const HAS_OFFERS = !!document.querySelector('.sticky-offer-step');

  // Product-level sold-out — stamped by Liquid as .is-sold-out on the pill.
  // Read ONCE so a runtime sold-out *variant* selection can't flip it.
  const PRODUCT_SOLD_OUT = !!(stickyATCBtn && stickyATCBtn.classList.contains('is-sold-out'));

  /* WHICH PRODUCT IS THIS PDP?
     The snippet already renders the sticky button as
        id="sticky-atc-btn-{{ product.id }}"
     so the product id is sitting in the DOM. No Liquid change needed to read it. */
  const PRODUCT_ID = stickyATCBtn
    ? (parseInt(String(stickyATCBtn.id).replace('sticky-atc-btn-', ''), 10) || 0)
    : 0;

  /* THE HANDOVER FLAG.
     "is THIS product in the cart", recomputed from /cart.js on every read. The
     cart is a cookie, so it survives a refresh, a hard refresh and a new tab —
     and removing the item correctly brings the pill back. */
  let _hasThisProduct = false;

  /* Nothing renders until the first /cart.js lands. Liquid already ships the bar
     hidden, so this costs nothing — but it prevents a flash of the ATC pill on
     refresh, because the browser restores your scroll position PAST the fold and
     applyVisibility() would otherwise run once before we know what's in the cart. */
  let _cartLoaded = false;

  let _cartState = { item_count: 0, total_price: 0 };
  let _lastItemCount = -1;
  let _lastTotalPrice = -1;
  let _lastItemsHash = '';

  /* Lets the first read tell "the cart is exactly what Liquid rendered" apart
     from "the cart actually moved". If it matches the render, #cart-comparetotal
     in the DOM is already correct and the product-fetch loop can be skipped. */
  let _isFirstPoll = true;

  let _productCache = {};
  let _selectedVariantId = stickyATCBtn ? stickyATCBtn.getAttribute('data-variant-id') : null;
  let _productData = null;
  let _productHandle = null;

  /* Seeded from the server-rendered count so applyVisibility() has real numbers
     before the first fetch resolves. */
  if (typeof CFG.cartItemCount === 'number') {
    _cartState.item_count = CFG.cartItemCount;
  }

  const isProductPage = /\/products\/[^\/?]+/.test(window.location.pathname);

  // ══════════════════════════════════════════════════════════════════════════
  // ATC LOADING STATE
  // --------------------------------------------------------------------------
  // The spinner is .sticky-atc-spinner — no t4s-loading__spinner, no pdp-loader.
  // Those classes dragged the theme's own positioning in with them, which is what
  // was shoving the spinner to the top of the pill.
  //   show -> the sticky ATC is tapped
  //   hide -> the cart read confirms the cart actually changed (the add landed)
  //   hide -> watchdog, so a failed add (sold out, network error) never leaves
  //           the button spinning forever
  // ══════════════════════════════════════════════════════════════════════════
  const ATC_LOADING_TIMEOUT = 5000;
  let _atcLoadingTimer = null;

  function setATCLoading(on) {
    if (!stickyATCBtn) return;
    if (stickyATCBtn.classList.contains('is-atc-loading') === on) return;

    stickyATCBtn.classList.toggle('is-atc-loading', on);
    stickyATCBtn.setAttribute('aria-busy', on ? 'true' : 'false');

    clearTimeout(_atcLoadingTimer);
    if (on) {
      _atcLoadingTimer = setTimeout(() => setATCLoading(false), ATC_LOADING_TIMEOUT);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // VARIANT AT CLICK TIME  (fix: sticky ATC ignored the selected size)
  // --------------------------------------------------------------------------
  // The sticky ATC is a standalone <a data-action-atc data-variant-id>. The
  // theme's add-to-cart reads data-variant-id off THIS node, so if the shopper
  // switched size/variant and our swatch observers hadn't wired up yet (or the
  // PDP uses a picker our observers don't recognise), the pill posted the Liquid
  // DEFAULT variant — "adds regardless of the size selected", and if that default
  // is unavailable the add silently fails and the spinner runs the full watchdog.
  //
  // So at the moment of the click (capture phase, before the theme handler) we
  // re-read the live selection from the most authoritative sources and stamp it
  // on the button. When we have this product's variant list we validate against
  // it so a neighbouring form (quick-view, upsell) can't leak the wrong id.
  // ══════════════════════════════════════════════════════════════════════════
  function getValidVariantIdSet() {
    const data = _productData || window.productData;
    if (data && Array.isArray(data.variants)) {
      return new Set(data.variants.map((v) => String(v.id)));
    }
    return null;
  }

  function resolveCurrentVariantId() {
    const validSet = getValidVariantIdSet();
    const candidates = [];

    // (a) The theme keeps the chosen variant in the product form's [name="id"],
    //     regardless of whether the picker is swatches, buttons or a select.
    document.querySelectorAll('form[action*="/cart/add"]').forEach((f) => {
      const checked = f.querySelector('[name="id"]:checked');
      if (checked && checked.value) candidates.push(checked.value);
      const field = f.querySelector('input[name="id"], select[name="id"]');
      if (field && field.value) candidates.push(field.value);
    });

    // (b) A selected node carrying a variant id directly (this store's size tabs
    //     + common T4S swatch states).
    document
      .querySelectorAll(
        '.size-option.selected[data-variant-id],' +
        '[data-swatch-item].is--selected[data-variant-id],' +
        '[data-variant-id].is--selected,' +
        '[data-variant-id].selected,' +
        '[data-variant-id][aria-checked="true"]'
      )
      .forEach((n) => {
        const v = n.getAttribute('data-variant-id');
        if (v) candidates.push(v);
      });

    // (c) Whatever our swatch listeners last resolved.
    if (_selectedVariantId) candidates.push(String(_selectedVariantId));

    // Prefer the first candidate that provably belongs to THIS product.
    if (validSet) {
      for (let i = 0; i < candidates.length; i++) {
        if (validSet.has(String(candidates[i]))) return candidates[i];
      }
    }
    // No list to validate against -> trust the first form-sourced candidate.
    if (candidates.length) return candidates[0];

    // Last resort: the Liquid default already on the button.
    return stickyATCBtn ? stickyATCBtn.getAttribute('data-variant-id') : null;
  }

  function wireATCLoadingState() {
    if (!stickyATCBtn) return;

    // Capture phase => this runs BEFORE the theme's delegated [data-action-atc]
    // handler, so the corrected variant id is in place when the theme reads it.
    stickyATCBtn.addEventListener(
      'click',
      () => {
        // Sold-out buttons already have pointer-events:none, but belt and braces.
        if (stickyATCBtn.classList.contains('is-sold-out')) return;

        const vid = resolveCurrentVariantId();
        if (vid) stickyATCBtn.setAttribute('data-variant-id', vid);

        setATCLoading(true);
        // The cart mutation interceptor + the [data-action-atc] backstop below
        // re-read the cart the moment the add lands, so the spinner clears fast.
      },
      true
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MONEY — mirrors Liquid's `money_without_trailing_zeros` so the price never
  //         reformats when the user taps a swatch.
  // ══════════════════════════════════════════════════════════════════════════
  const MONEY_FORMAT = CFG.moneyFormat || '₹{{amount}}';

  // Set to false (and swap the Liquid filter to `| money`) if you want ₹1,355.00
  const STRIP_TRAILING_ZEROS = true;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(cents) || cents === null) return '0';
    const parts = (cents / 100.0).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents) {
    try {
      const ph = MONEY_FORMAT.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!ph) return MONEY_FORMAT;

      let value;
      switch (ph[1]) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0); break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.'); break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      if (STRIP_TRAILING_ZEROS) value = value.replace(/[.,]00$/, '');
      return MONEY_FORMAT.replace(/\{\{\s*\w+\s*\}\}/, value);
    } catch (e) {
      return '₹' + Math.round(cents / 100);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DRAWER OPEN — forward sticky clicks to the theme's real header trigger
  // --------------------------------------------------------------------------
  // #nin_web_cart opens the mini-cart via a theme handler bound to it, and has
  // NO data-drawer-options — so this theme does NOT open the drawer from a
  // generic [data-drawer-options] listener. Our sticky anchors carry
  // data-drawer-options but the theme ignores it on them, so the click follows
  // href="/cart" and NAVIGATES (the page-load-then-cart flash).
  //
  // Fix: forward the tap to a#nin_web_cart (the id is reused on 3 nested nodes —
  // invalid HTML — so a# pins the ANCHOR) and preventDefault so nothing
  // navigates. If the native trigger isn't found, or a synthetic click doesn't
  // open it, we let the href run to /cart as a last resort — the icon is never
  // dead.
  // ══════════════════════════════════════════════════════════════════════════
  function getNativeCartTrigger() {
    return document.querySelector('a#nin_web_cart');
  }

  function forwardToDrawer(e) {
    const native = getNativeCartTrigger();
    if (!native) return; // no trigger -> let the href navigate to /cart

    /* iOS JITTER FIX (3/3) — opening the drawer scroll-locks <body> and iOS may
       collapse the URL bar, both of which fire scroll events. Hold the bar's
       current state across that so it can't toggle mid-tap. */
    freezeVisibility(DRAWER_FREEZE_MS);

    e.preventDefault();   // kill the /cart navigation (the flash)
    native.click();       // theme's own handler opens the already-warmed drawer
  }

  function wireDrawerForwarders() {
    ['sticky-cart-icon', 'view-cart-button'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('click', forwardToDrawer);
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // STICKY HEADER OFFSET  (reveal threshold)
  // --------------------------------------------------------------------------
  // When this bar was built the header did NOT stick, so "floating ATC is gone"
  // == "its bottom cleared the top of the viewport" (bottom < 0). With a sticky
  // header the ATC disappears BEHIND the header a full header-height earlier,
  // leaving a stretch of scroll with no add-to-cart on screen at all. The
  // threshold is therefore the header's bottom edge, not 0.
  //
  // The element is resolved ONCE (getComputedStyle is not cheap); only the rect
  // is read per frame, which is correct even for headers that shrink on scroll
  // or sit under an announcement bar that scrolls away.
  // ══════════════════════════════════════════════════════════════════════════
  const HEADER_FALLBACK_SELECTORS = [
    '#shopify-section-header',
    '.t4s-header__wrapper',
    '#t4s-header',
    'header'
  ];

  let _headerEl = null;

  function isStuck(el) {
    const pos = getComputedStyle(el).position;
    return pos === 'sticky' || pos === 'fixed';
  }

  function resolveStickyHeader() {
    if (STICKY_HEADER_SELECTOR) {
      const pinned = document.querySelector(STICKY_HEADER_SELECTOR);
      if (pinned) return pinned;
    }

    // 1) a candidate that is itself sticky/fixed
    for (const sel of HEADER_FALLBACK_SELECTORS) {
      const el = document.querySelector(sel);
      if (el && isStuck(el)) return el;
    }

    // 2) commonly the sticky node is an INNER bar inside the header section,
    //    while the section wrapper itself is static. Bounded scan — one-time
    //    cost at init, never per frame.
    for (const sel of HEADER_FALLBACK_SELECTORS) {
      const root = document.querySelector(sel);
      if (!root) continue;
      const nodes = root.querySelectorAll('*');
      const limit = Math.min(nodes.length, 150);
      for (let i = 0; i < limit; i++) {
        if (isStuck(nodes[i])) return nodes[i];
      }
    }

    // 3) nothing sticky -> plain <header>. Its rect.bottom goes negative once it
    //    scrolls away and we clamp at 0, i.e. the original behaviour.
    return document.querySelector('header') || null;
  }

  function getHeaderOffset() {
    if (!_headerEl || !_headerEl.isConnected) _headerEl = resolveStickyHeader();
    if (!_headerEl) return 0;
    return Math.max(0, _headerEl.getBoundingClientRect().bottom);
  }

  // Header height changes on rotate / resize -> re-resolve lazily.
  window.addEventListener('resize', () => { _headerEl = null; }, { passive: true });

  // ══════════════════════════════════════════════════════════════════════════
  // VISIBILITY
  // ══════════════════════════════════════════════════════════════════════════

  /* Sticky latch for the fold test. Once revealed the bar stays revealed until
     the floating ATC comes back a clear FOLD_HYSTERESIS px below the threshold,
     so a one-frame scroll wobble can't flip it. */
  let _afterFold = false;

  function isAfterFold() {
    const nativeATC = document.getElementById('nin_pdp_pd_atc');

    if (nativeATC) {
      const r = nativeATC.getBoundingClientRect();

      /* Floating ATC hidden by other code (display:none -> all-zero rect):
         we are past it by definition. */
      if (r.width === 0 && r.height === 0) {
        _afterFold = true;
        return true;
      }

      const threshold = getHeaderOffset();
      _afterFold = _afterFold
        ? r.bottom <= threshold + FOLD_HYSTERESIS   // already revealed -> hold
        : r.bottom <= threshold;                    // reveal exactly at handoff
      return _afterFold;
    }

    // No floating ATC on this page -> scroll-ratio heuristic, same dead band.
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const ratio = scrollHeight > 0 ? scrollTop / scrollHeight : 0;
    _afterFold = _afterFold ? ratio > 0.10 : ratio > 0.12;
    return _afterFold;
  }

  /* STATE FREEZE — hold the bar exactly as it is for a moment (drawer opening).
     A trailing re-apply is scheduled so nothing is left stale once it lifts. */
  let _freezeUntil = 0;
  let _freezeTimer = null;

  function freezeVisibility(ms) {
    _freezeUntil = Date.now() + ms;
    clearTimeout(_freezeTimer);
    _freezeTimer = setTimeout(applyVisibility, ms + 20);
  }

  function applyVisibility() {
    /* Hold everything until the first /cart.js lands. On a refresh the browser
       restores the scroll position past the fold, so without this we'd reveal
       the ATC pill for ~300ms before flipping to View Cart. Liquid ships the bar
       hidden, so waiting costs nothing. */
    if (!_cartLoaded) return;

    // Drawer opening -> don't let a reflow-driven scroll event re-toggle us.
    if (Date.now() < _freezeUntil) return;

    const { item_count, total_price } = _cartState;
    const hasItems = item_count > 0;

    if (PRODUCT_MODE) {
      const soldOut = PRODUCT_SOLD_OUT;

      /* HANDOVER TO VIEW CART:
         • Available product -> when THIS product is in the cart.
         • Sold-out product  -> when the cart has ANY items (Enhancement 2:
           landing on an OOS PDP with items already in cart shows View Cart).
           A sold-out product can never itself be in the cart, so "has items" is
           the correct trigger for it. */
      const handedOver = soldOut ? hasItems : (_hasThisProduct && hasItems);

      /* Offer callout must be visible from the first fold when the cart is empty
         (QA point 4). Keeps the bar revealed even at the very top. */
      const showOfferFromTop = HAS_OFFERS;

      /* ── BAR REVEAL ──────────────────────────────────────────────────────
         Toggling the PARENT is what lets the offer steps stay untouched: the
         correct milestone is already painted (server-side) inside the hidden
         container, so it's live the instant the bar reveals. */
      const revealed = handedOver || isAfterFold() || showOfferFromTop;
      if (stickyBar) stickyBar.classList.toggle('is-bar-hidden', !revealed);

      // Handover -> View Cart owns the bar.
      if (handedOver) {
        stickyATC.classList.add('hidden');
        if (stickyCart) stickyCart.classList.remove('hidden');
        return;
      }

      // Not handed over -> View Cart stays hidden.
      if (stickyCart) stickyCart.classList.add('hidden');

      if (soldOut) {
        /* Reaches here only with an EMPTY cart (items -> handedOver -> View Cart
           above). Enhancement 1: the OOS pill mirrors the ATC pill — hidden at
           the top, revealed at the header handoff, full width, no cart icon. */
        stickyATC.classList.toggle('hidden', !isAfterFold());
        if (stickyForm) stickyForm.classList.remove('has-items');
        return;
      }

      // Available product -> ATC pill at the header handoff.
      stickyATC.classList.toggle('hidden', !isAfterFold());

      // Cart already had items when we landed (added on an earlier PDP) ->
      // cart icon + count bubble next to the pill.
      if (stickyForm) stickyForm.classList.toggle('has-items', hasItems);
      if (stickyCartCount) stickyCartCount.textContent = item_count;
      return;
    }

    // ── CART MODE ────────────────────────────────────────────────────────────
    const showCart = hasItems && total_price > 0;
    if (stickyCart) stickyCart.classList.toggle('hidden', !showCart);

    // No offer callouts (gift-card PDP) and no View Cart -> nothing to show.
    if (stickyBar) stickyBar.classList.toggle('is-bar-hidden', !HAS_OFFERS && !showCart);
  }

  /* rAF-throttled. isAfterFold() calls getBoundingClientRect(), which forces a
     layout read — running that on every raw scroll event is a real jank source
     on a long PDP. One read per frame, max. */
  let _scrollTicking = false;
  window.addEventListener('scroll', () => {
    if (_scrollTicking) return;
    _scrollTicking = true;
    requestAnimationFrame(() => {
      applyVisibility();
      _scrollTicking = false;
    });
  }, { passive: true });

  // ══════════════════════════════════════════════════════════════════════════
  // CART READ
  // ══════════════════════════════════════════════════════════════════════════
  function buildItemsHash(items) {
    return items.map((i) => `${i.variant_id}:${i.quantity}`).join('|');
  }

  async function updateCartView() {
    try {
      const res = await fetch('/cart.js');
      const cartData = await res.json();
      const { item_count, total_price, items, original_total_price } = cartData;

      /* ── COMPARE-AT: DON'T REDO LIQUID'S WORK ──────────────────────────────
         The snippet already server-rendered #cart-comparetotal for whatever the
         cart looked like at page render. If this FIRST read comes back identical
         to that render, the number in the DOM is still correct — skip the
         product-fetch loop entirely rather than recomputing the same value.

         Any later read, or a first read that reveals the cart actually moved
         between render and load, recomputes normally.

         Why JS can't be dropped altogether: /cart.js line items do NOT expose
         compare_at_price, so a NEW item added via AJAX after render still needs
         /products/{handle}.js to price its strikethrough. */
      const matchesServerRender =
        _isFirstPoll &&
        item_count === CFG.cartItemCount &&
        total_price === CFG.cartTotalPrice;
      _isFirstPoll = false;

      /* IS THIS PRODUCT IN THE CART?
           • survives refresh / hard refresh / new tab (the cart is a cookie)
           • self-corrects — remove the item and the ATC pill properly returns
           • still catches an add from ANY source (sticky ATC, native ATC,
             quick-view, an upsell app), because they all end up in /cart.js */
      _hasThisProduct = PRODUCT_ID
        ? items.some((i) => i.product_id === PRODUCT_ID)
        : false;
      _cartLoaded = true;

      const itemsHash = buildItemsHash(items);
      const cartChanged =
        item_count !== _lastItemCount ||
        total_price !== _lastTotalPrice ||
        itemsHash !== _lastItemsHash;

      _cartState = { item_count, total_price };
      _lastItemCount = item_count;
      _lastTotalPrice = total_price;
      _lastItemsHash = itemsHash;

      if (cartQuantityElem) cartQuantityElem.textContent = item_count;
      if (cartTotalElem) cartTotalElem.textContent = formatMoney(total_price);

      if (cartChanged) {
        // The cart moved -> whatever the button was waiting on has landed.
        setATCLoading(false);

        // Compare-at total is only worth computing when View Cart will actually
        // be shown — AND when Liquid hasn't already painted the right number.
        const needCompare =
          cartCompareTotalElem &&
          (!PRODUCT_MODE || _hasThisProduct || PRODUCT_SOLD_OUT) &&
          !matchesServerRender;

        if (needCompare) {
          let totalComparePrice = 0;

          // Fire the distinct, not-yet-cached product fetches CONCURRENTLY
          // instead of one-at-a-time — the sequential await inside the loop was
          // the main source of the delay whenever the cart held >1 product.
          const uniqueHandles = [...new Set(items.map((i) => i.handle))].filter(
            (h) => !_productCache[h]
          );

          await Promise.all(
            uniqueHandles.map(async (handle) => {
              try {
                const productRes = await fetch(`/products/${handle}.js`);
                _productCache[handle] = await productRes.json();
              } catch (err) {
                // left uncached; the per-item fallback below covers it
              }
            })
          );

          for (const item of items) {
            const productData = _productCache[item.handle];
            const variant = productData?.variants?.find((v) => v.id === item.variant_id);
            if (variant) {
              const priceToUse = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
              totalComparePrice += priceToUse * item.quantity;
            } else {
              // fetch failed or variant missing -> fall back to the paid price,
              // which simply contributes no "saving" for that line.
              totalComparePrice += item.price * item.quantity;
            }
          }

          // Same fallback ladder as the Liquid block in the snippet — keep in sync.
          if (totalComparePrice > 0 && totalComparePrice > total_price) {
            cartCompareTotalElem.textContent = formatMoney(totalComparePrice);
          } else if (original_total_price > total_price) {
            cartCompareTotalElem.textContent = formatMoney(original_total_price);
          } else {
            cartCompareTotalElem.textContent = '';
          }
        }

        updatePromoRibbon(cartData);
      }

      applyVisibility();

      /* offer-call.js listens for this instead of running its own duplicate 1s
         /cart.js poll. Halves the cart requests per session. */
      document.dispatchEvent(new CustomEvent('sticky-cart:updated', {
        detail: { item_count, total_price, items }
      }));
    } catch (err) {
      /* If /cart.js is unreachable we must NOT leave the bar stuck hidden behind
         the _cartLoaded gate. Unblock and fall back to the ATC pill. The Liquid-
         rendered compare-at total simply stays as-is, which is the best available
         answer when we can't read the cart. */
      _isFirstPoll = false;
      _cartLoaded = true;
      applyVisibility();
      console.error('[sticky-cart] cart fetch failed', err);
    }
  }

  // Backstop only — if an add ever slips past the mutation interceptor (e.g. the
  // theme cached a reference to window.fetch before we patched it), still recover
  // the spinner teardown + View Cart handover after the add settles.
  document.addEventListener('click', (e) => {
    if (e.target.closest && e.target.closest('[data-action-atc]')) {
      setTimeout(updateCartView, 1200);
    }
  });

  // ══════════════════════════════════════════════════════════════════════════
  // PRODUCT DATA (PDP only)
  // ══════════════════════════════════════════════════════════════════════════
  function extractProductHandle() {
    const urlHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
    if (urlHandle) return urlHandle;

    const productContainer = document.querySelector('[data-product-id]');
    if (productContainer?.dataset.productHandle) return productContainer.dataset.productHandle;

    const metaTag = document.querySelector('meta[property="og:url"]');
    if (metaTag) {
      const handle = metaTag.getAttribute('content')?.split('/products/')[1]?.split('?')[0];
      if (handle) return handle;
    }
    return null;
  }

  async function fetchProductData() {
    if (window.productData && window.productData.variants) {
      _productData = window.productData;
      return true;
    }

    for (let i = 0; i < 6; i++) {   // 600ms cap, then fall through to fetch
      await new Promise((r) => setTimeout(r, 100));
      if (window.productData && window.productData.variants) {
        _productData = window.productData;
        return true;
      }
    }

    _productHandle = extractProductHandle();
    if (_productHandle) {
      try {
        const res = await fetch(`/products/${_productHandle}.js`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        _productData = await res.json();
        return true;
      } catch (err) {
        log('❌ handle fetch failed:', err.message);
      }
    }

    const selectors = [
      'script[type="application/json"][data-product-data]',
      'script[type="application/ld+json"]',
      'script[data-product]',
      'script#product-data',
      'script#productData'
    ];
    for (const selector of selectors) {
      const tag = document.querySelector(selector);
      if (!tag) continue;
      try {
        const data = JSON.parse(tag.textContent);
        if (data.variants || data.products) {
          _productData = data.variants ? data : data.products?.[0];
          return true;
        }
      } catch (e) { /* noop */ }
    }

    if (window.Shopify?.theme?.products) {
      _productData = window.Shopify.theme.products[0];
      return true;
    }

    // NOTE: the old constructProductDataFromPage() fabricated variant IDs
    // (1000, 1001, ...) from swatch nodes. If that path ever fired, the sticky
    // ATC posted a variant that does not exist. Removed on purpose — bailing
    // here leaves the Liquid-rendered (real) default variant id on the button.
    log('❌ could not load product data — keeping default variant id');
    return false;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // VARIANT -> BUTTON
  // ══════════════════════════════════════════════════════════════════════════
  function mapVariantValueToId() {
    if (!_productData || !_productData.variants) return;

    const selectedOptions = Array.from(
      document.querySelectorAll('[data-swatch-item].is--selected')
    ).map((el) => el.getAttribute('data-value'));

    const matched = _productData.variants.find((variant) =>
      variant.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (matched) {
      _selectedVariantId = matched.id;
      updateStickyATCVariant(matched.id, matched);
    } else {
      log('❌ no exact variant match', selectedOptions);
    }
  }

  function updateStickyATCVariant(variantId, variantData) {
    if (!stickyATCBtn) return;

    stickyATCBtn.setAttribute('data-variant-id', variantId);

    const variant = variantData || _productData?.variants?.find((v) => v.id == variantId);
    if (!variant) return;

    updateStickyPrice(variant);
    updateButtonAvailability(variant.available !== false);
  }

  function updateStickyPrice(variant) {
    if (!variant || !stickyATCBtn) return;

    const priceEl = stickyATCBtn.querySelector('.selling-price');
    const compareEl = stickyATCBtn.querySelector('.compare-price'); // not rendered by default

    if (priceEl) priceEl.textContent = formatMoney(variant.price);

    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  function updateButtonAvailability(isAvailable) {
    if (!stickyATCBtn) return;
    const textSpan = stickyATCBtn.querySelector('.t4s-text-pr');

    if (isAvailable) {
      stickyATCBtn.style.opacity = '1';
      stickyATCBtn.style.pointerEvents = 'auto';
      stickyATCBtn.classList.remove('disabled', 'sold-out', 'is-sold-out');
      if (textSpan) textSpan.textContent = 'Add to cart'; // CSS uppercases it
    } else {
      // 60% opacity + "Out of stock" — matches the design and the Liquid
      // first-paint state for a sold-out pill.
      stickyATCBtn.style.opacity = '0.6';
      stickyATCBtn.style.pointerEvents = 'none';
      stickyATCBtn.classList.add('disabled', 'sold-out', 'is-sold-out');
      if (textSpan) textSpan.textContent = 'Out of stock';
      setATCLoading(false); // never leave a sold-out button spinning
    }
    // .is-sold-out hides the "-" separator and the price via CSS.
  }

  function listenForSwatchClicks() {
    const swatchItems = document.querySelectorAll('[data-swatch-item]');

    if (swatchItems.length === 0) {
      listenForVariantChanges();
      return;
    }

    swatchItems.forEach((swatch) => {
      swatch.addEventListener('click', () => setTimeout(mapVariantValueToId, 50));
    });

    if (window.MutationObserver) {
      const observer = new MutationObserver(() => mapVariantValueToId());
      swatchItems.forEach((swatch) => {
        observer.observe(swatch, { attributes: true, attributeFilter: ['class'] });
      });
    }

    if (document.querySelector('[data-swatch-item].is--selected')) mapVariantValueToId();
  }

  function listenForVariantChanges() {
    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect) {
      variantSelect.addEventListener('change', (e) => {
        _selectedVariantId = e.target.value;
        updateStickyATCVariant(e.target.value);
      });
      _selectedVariantId = variantSelect.value;
      updateStickyATCVariant(variantSelect.value);
    }

    document.addEventListener('variant-change', (e) => {
      if (e.detail?.variant) {
        _selectedVariantId = e.detail.variant.id;
        updateStickyATCVariant(e.detail.variant.id, e.detail.variant);
      }
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // PROMO RIBBON (unchanged — still no-ops unless #promoRibbon exists)
  // ══════════════════════════════════════════════════════════════════════════
  function updatePromoRibbon(cartData) {
    const promoRibbonElem = document.getElementById('promoRibbon');
    if (!promoRibbonElem) return;

    const { items } = cartData;

    let singSkuCount = 0;
    items.forEach((item) => {
      const itemElement = document.querySelector(`[data-pid="${item.product_id}"]`);
      if (!itemElement || !itemElement.classList.contains('pr-isbundle')) {
        singSkuCount += item.quantity;
      }
    });

    const countQty1 = CFG.minQty1 || 0;
    const countQty2 = CFG.minQty2 || 0;
    const couponCode1 = CFG.couponCode1 || '';
    const couponCode2 = CFG.couponCode2 || '';
    const couponPer1 = CFG.couponPer1 || '';
    const couponPer2 = CFG.couponPer2 || '';
    const st1prefix = CFG.st1prefix || '';
    const st1mdl = CFG.st1mdl || '';
    const st2prefix = CFG.st2prefix || '';
    const st2mdl = CFG.st2mdl || '';
    const st3prefix = CFG.st3prefix || '';
    const st3mdl = CFG.st3mdl || '';

    let promoMessage = '';

    if (countQty1 === 0 && countQty2 > 0) {
      if (singSkuCount === 0)
        promoMessage = `<div>${st1prefix} <b>${countQty2}</b> ${st1mdl} <b>${couponPer2}</b></div>`;
      else if (singSkuCount < countQty2)
        promoMessage = `<div>${st2prefix} <b>${countQty2 - singSkuCount}</b> ${st2mdl} <b>${couponPer2}</b></div>`;
      else
        promoMessage = `<div>${st3prefix} <b>${couponCode2}</b>${st3mdl} <b>${couponPer2}</b></div>`;
    } else if (countQty2 === 0 && countQty1 > 0) {
      if (singSkuCount === 0)
        promoMessage = `<div>${st1prefix} <b>${countQty1}</b> ${st1mdl} <b>${couponPer1}</b></div>`;
      else if (singSkuCount < countQty1)
        promoMessage = `<div>${st2prefix} <b>${countQty1 - singSkuCount}</b> ${st2mdl} <b>${couponPer1}</b></div>`;
      else
        promoMessage = `<div>${st3prefix} <b>${couponCode1}</b>${st3mdl} <b>${couponPer1}</b></div>`;
    } else {
     if (singSkuCount === 0)
        promoMessage = `<div>${st1prefix} <b>${countQty1}</b> ${st1mdl} <b>${couponPer1}</b></div>`;
      else if (singSkuCount < countQty1)
        promoMessage = `<div>${st2prefix} <b>${countQty1 - singSkuCount}</b> ${st2mdl} <b>${couponPer1}</b></div>`;
      else if (singSkuCount >= countQty1 && singSkuCount < countQty2)
        promoMessage = `<div>${st3prefix} <b>${couponCode1}</b>${st3mdl} <b>${couponPer1}</b> or ${st2prefix} <b>${
          countQty2 - singSkuCount
        }</b> ${st2mdl} <b>${couponPer2}</b></div>`;
      else if (singSkuCount >= countQty2)
        promoMessage = `<div>${st3prefix} <b>${couponCode2}</b>${st3mdl} <b>${couponPer2}</b></div>`;
    }

    promoRibbonElem.innerHTML = promoMessage;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // CART MUTATION INTERCEPTOR
  // --------------------------------------------------------------------------
  // Fire the instant the real cart endpoints return, instead of polling every
  // 5s. Frees the network/main thread when the user taps (faster drawer), and
  // tears the spinner down + hands to View Cart the moment the add lands.
  //
  // The regex only matches /cart/add|change|update|clear — it does NOT match
  // /cart.js or /products/*.js, so updateCartView()'s own reads never re-trigger
  // it (no recursion). __stickyHooked guards prevent double-patching.
  // ══════════════════════════════════════════════════════════════════════════
  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$|#)/;

  function hookCartMutations(onMutation) {
    const fire = () => setTimeout(onMutation, 50); // server state is committed

    // fetch (T4S AJAX add uses this)
    const origFetch = window.fetch;
    if (origFetch && !origFetch.__stickyHooked) {
      window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const isMut = CART_MUTATION_RE.test(String(url));
        return origFetch.apply(this, arguments).then((res) => { if (isMut) fire(); return res; });
      };
      window.fetch.__stickyHooked = true;
    }

    // XMLHttpRequest (some builds / apps still add via XHR)
    const origOpen = XMLHttpRequest.prototype.open;
    if (origOpen && !origOpen.__stickyHooked) {
      XMLHttpRequest.prototype.open = function (m, url) {
        this.__stickyMut = CART_MUTATION_RE.test(String(url || ''));
        return origOpen.apply(this, arguments);
      };
      const origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        if (this.__stickyMut) this.addEventListener('loadend', fire);
        return origSend.apply(this, arguments);
      };
      XMLHttpRequest.prototype.open.__stickyHooked = true;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // INIT
  // ══════════════════════════════════════════════════════════════════════════
  log('📌 sticky bar init — mode:', PRODUCT_MODE ? 'PRODUCT' : 'CART', '| product:', PRODUCT_ID);

  wireDrawerForwarders();   // sticky icon + View Cart -> native drawer, no navigation

  if (PRODUCT_MODE) {
    wireATCLoadingState();

    if (isProductPage) {
      (async () => {
        const ok = await fetchProductData();
        if (ok) listenForSwatchClicks();
      })();
    }
  }

  applyVisibility();          // no-ops until _cartLoaded — deliberate
  updateCartView();           // one read to sync with the server render

  hookCartMutations(updateCartView);   // event-driven from here on — no idle poll

  // Only re-sync when the tab regains focus (covers a change made in another tab).
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) updateCartView();
  });
});
